import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ButtonV2,
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
} from '@labrav/react-components'
import { faUpload, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Chip } from '@mui/material'
import { useDispatch, useSelector } from '../../../../../../store'
import {
  MarketplaceFieldMapping,
  MarketplaceTemplatesDataFieldMappings,
} from '../../../../../../modules/marketplaceMappingManager/types'
import {
  setMarketplaceReferralTemplate,
  updateMarketplaceTemplateSectionField,
} from '../../../../../../modules/marketplaceMappingManager/action'
import { mergeNestedSalesforceFieldsMappingPatch } from './MarketplaceTabFieldConfiguration'
import {
  FileUploadWithLoader,
  getFileLinksFromUrl,
  defaultNow,
  bucketName,
  useAccountId,
  LoadingTypes,
  isLoading as Loading,
} from '@labrav/common'
import { ShortenedName } from '../../../../../../common/components/ShortenedName/ShortenedName'
import { useStyles } from '../../MarketplaceTemplateTableContainer.styles'
import { useStore } from 'react-redux'
import { RootState } from '../../../../../../store'
import {
  eulaFileUploadButtonText,
  eulaFileUploadContainerTitle,
  fileFormatIncorrectText,
  mappingManagerEulaFileUploadDescription,
} from '../../../../../../common/utils/productCreationUtil'

function getFieldMappingSlice(
  fieldMappings: Record<string, unknown> | null | undefined,
  fieldKey: string
): Record<string, unknown> | undefined {
  if (!fieldMappings || typeof fieldMappings !== 'object') return undefined
  const fm = fieldMappings as Record<string, unknown>
  const nested =
    fm.fields && typeof fm.fields === 'object'
      ? (fm.fields as Record<string, unknown>)
      : undefined
  const byMappingKey =
    nested?.fieldsMapping &&
    typeof nested.fieldsMapping === 'object' &&
    fieldKey in (nested.fieldsMapping as object)
      ? (nested.fieldsMapping as Record<string, unknown>)[fieldKey]
      : undefined
  if (byMappingKey && typeof byMappingKey === 'object') {
    return byMappingKey as Record<string, unknown>
  }
  const bySnakeKey =
    nested?.fields_mapping &&
    typeof nested.fields_mapping === 'object' &&
    fieldKey in (nested.fields_mapping as object)
      ? (nested.fields_mapping as Record<string, unknown>)[fieldKey]
      : undefined
  if (bySnakeKey && typeof bySnakeKey === 'object') {
    return bySnakeKey as Record<string, unknown>
  }
  const top = fm[fieldKey]
  if (top && typeof top === 'object') {
    return top as Record<string, unknown>
  }
  return undefined
}

export const MarketplaceMappingManagerFileUpload = React.memo(
  ({
    fieldKey,
    disabled,
  }: {
    fieldKey: keyof MarketplaceTemplatesDataFieldMappings
    disabled: boolean
  }) => {
    const { classes } = useStyles()
    const dispatch = useDispatch()
    const accountId = useAccountId({ product: 'flyout' }) || ''
    const reduxStore = useStore<RootState>()
    const isFileUploading = useSelector<boolean>(state =>
      Loading(state.loading)(LoadingTypes.FILE_PROCESSING_LOADER)
    )

    const marketplaceFieldMappings = useSelector(
      state =>
        state.marketplaceMappingManager?.templateData?.templateData
          ?.fieldMappings as MarketplaceTemplatesDataFieldMappings | undefined
    )

    const [openModal, setOpenModal] = useState(false)
    const [eulaErr, setEulaErr] = useState('')
    const [tempUploadedFiles, setTempUploadedFiles] = useState<string[]>([])
    const [showAll, setShowAll] = useState(false)

    const getDefaultFileUrls = useCallback(() => {
      const slice = getFieldMappingSlice(
        marketplaceFieldMappings as Record<string, unknown> | undefined,
        fieldKey as string
      )
      const defaultValue = slice?.defaultValue as string | string[] | undefined
      return Array.isArray(defaultValue)
        ? defaultValue
        : defaultValue
          ? [defaultValue]
          : []
    }, [fieldKey, marketplaceFieldMappings])

    useEffect(() => {
      setTempUploadedFiles(getDefaultFileUrls())
    }, [getDefaultFileUrls])

    const handleOpenFileUploadModal = useCallback(() => {
      setTempUploadedFiles(getDefaultFileUrls())
      setOpenModal(true)
    }, [getDefaultFileUrls])

    const handleCloseModal = useCallback(() => {
      setTempUploadedFiles(getDefaultFileUrls())
      setOpenModal(false)
      setEulaErr('')
    }, [getDefaultFileUrls])

    const updateEulaUrl = useCallback((fileUrl: string, index = 0) => {
      setTempUploadedFiles(prev => {
        const updated = [...prev]
        if (index < updated.length) updated[index] = fileUrl
        else updated.push(fileUrl)
        return updated
      })
    }, [])

    const deleteEulaUrl = useCallback((index: number) => {
      setTempUploadedFiles(prev => {
        const updated = [...prev]
        updated.splice(index, 1)
        return updated
      })
    }, [])

    const updatedExistingFileLinks = useMemo(() => {
      return getFileLinksFromUrl(getDefaultFileUrls(), true)
    }, [getDefaultFileUrls])

    const uploadedFileNames = useMemo(
      () => updatedExistingFileLinks.map(file => file.name),
      [updatedExistingFileLinks]
    )

    const handleUploadEULAFiles = useCallback(() => {
      dispatch(
        updateMarketplaceTemplateSectionField(
          'fields' as keyof MarketplaceTemplatesDataFieldMappings,
          {
            fieldsMapping: {
              ...(marketplaceFieldMappings?.fields?.fieldsMapping as Record<
                string,
                MarketplaceFieldMapping
              >),
              [fieldKey]: {
                ...(marketplaceFieldMappings?.fields?.fieldsMapping?.[
                  fieldKey
                ] as MarketplaceFieldMapping),
                defaultValue: tempUploadedFiles,
                isMapped: tempUploadedFiles.length > 0,
              },
            },
          } as any
        )
      )
      setOpenModal(false)
    }, [dispatch, fieldKey, reduxStore, tempUploadedFiles])

    const handleShowAllClick = useCallback(() => setShowAll(true), [])

    const onDelete = useCallback(
      (fileNameToDelete: string) => {
        const updated = getDefaultFileUrls().filter(file => {
          const { name } = getFileLinksFromUrl([file], true)[0] || {}
          return name !== fileNameToDelete
        })
        const updates = {
          defaultValue: updated,
          isMapped: updated.length > 0,
        }
        const current =
          reduxStore.getState().marketplaceMappingManager.templateData
            .templateData
        const merged = mergeNestedSalesforceFieldsMappingPatch(
          current,
          fieldKey as string,
          updates
        )
        if (merged) {
          dispatch(setMarketplaceReferralTemplate(merged))
          return
        }
        dispatch(updateMarketplaceTemplateSectionField(fieldKey, updates))
      },
      [dispatch, fieldKey, getDefaultFileUrls, reduxStore]
    )

    const displayCount = 1
    const remainingCount = uploadedFileNames.length - displayCount
    const displayedValues = showAll
      ? uploadedFileNames
      : uploadedFileNames.slice(0, displayCount)

    const hasFiles = getDefaultFileUrls().length > 0

    return (
      <>
        {!hasFiles ? (
          <ButtonV2
            onClick={handleOpenFileUploadModal}
            disabled={disabled}
            className={classes.fileUploadBtn}
            styleType="outlined"
            smallVariant
          >
            Upload files
          </ButtonV2>
        ) : (
          <div className={classes.selectedFileChipContainer}>
            <div className={classes.selectedFileChipInnerContainer}>
              <div className={classes.chipContainer}>
                {displayedValues.map(option => (
                  <Chip
                    key={option}
                    variant="outlined"
                    className={classes.chip}
                    label={
                      <ShortenedName
                        name={option}
                        length={12}
                        tooltipPlacement="top"
                        isV2
                      />
                    }
                    onDelete={() => onDelete(option)}
                    deleteIcon={
                      <FontAwesomeIcon
                        icon={faXmark}
                        width="18px"
                        height="18px"
                      />
                    }
                  />
                ))}
                {remainingCount > 0 && !showAll && (
                  <Chip
                    label={`+${remainingCount}`}
                    className={classes.chip}
                    onClick={handleShowAllClick}
                  />
                )}
              </div>
            </div>
            <LabraToolTipV2 tooltipPlacement="top" description="Upload files">
              <ButtonV2
                onClick={handleOpenFileUploadModal}
                disabled={disabled}
                className={classes.fileUploadIcon}
                styleType="outlined"
                smallVariant
              >
                <FontAwesomeIcon icon={faUpload} />
              </ButtonV2>
            </LabraToolTipV2>
          </div>
        )}

        <ModalConfirmV2
          title="Upload EULA"
          icon={<FontAwesomeIcon icon={faUpload} />}
          onlyCloseModal
          onCloseModal={handleCloseModal}
          onDecline={handleCloseModal}
          open={openModal}
          onAccept={handleUploadEULAFiles}
          acceptDisabled={isFileUploading || tempUploadedFiles.length === 0}
          titleCancelButton="Cancel"
          titleAcceptButton="Submit"
          variant={ModalSizeVariants.large}
          hideCancelButton={false}
          content={
            <div className={classes.fileUploadContainer}>
              <FileUploadWithLoader
                keyName="custom-eula-file-upload"
                partnerId={accountId}
                existingFileLinks={tempUploadedFiles}
                assetType="eula_files"
                folder="product_eula_files"
                fileUploadDescription={mappingManagerEulaFileUploadDescription}
                quantity={5}
                fileUploadType="txt/pdf"
                unit="MB"
                uploadFileSize={10}
                acceptedFormats={['.pdf', '.txt']}
                updateUrl={updateEulaUrl}
                deleteUrl={deleteEulaUrl}
                defaultNow={defaultNow}
                fileUploadButtonText={eulaFileUploadButtonText}
                fileFormatIncorrectText={fileFormatIncorrectText}
                fileUploadContainerTitle={eulaFileUploadContainerTitle}
                bucketName={bucketName}
                isMultiFileUpload
                updateErr={setEulaErr}
                uploadError={eulaErr}
                viewOnly={false}
                checkDuplicate
                showRealFileName
              />
            </div>
          }
        />
      </>
    )
  }
)
